import React, { useEffect, useMemo, useState } from 'react';
import { BaseModal, ClientCombobox, CollaboratorCombobox } from '@/components/shared';
import { useClientsQuery } from '@/features/Clients/Data';
import { useRegrasClienteQuery } from '../../../../hooks/data';
import type { ItemPreviaImportacaoGlpi } from '../../../../services';
import {
  DETALHES_SITUACAO_ATIVO,
  SITUACAO_ATIVO_LABELS,
  SITUACAO_POR_STATUS,
  SITUACOES_ATIVO,
  STATUS_POR_SITUACAO,
  TIPO_ATIVO_LABELS,
  TIPOS_ATIVO_CADASTRO,
  rotuloPatrimonio,
  type SituacaoAtivo,
} from './ativos.definitions';
import { valorFinal, type DecisaoLocal } from './ImportarGlpiLinha';

interface AjustarItemImportacaoModalProps {
  item: ItemPreviaImportacaoGlpi | null;
  decisao: DecisaoLocal | undefined;
  onSalvar: (ajustes: Record<string, string | null>) => void;
  onClose: () => void;
}

const CAMPOS = [
  'tipo',
  'patrimonio',
  'nSerie',
  'modelo',
  'fabricante',
  'statusCustodia',
  'situacaoDetalhe',
  'propriedade',
  'clienteProprietarioId',
  'projetoClienteId',
  'colaboradorAtualId',
] as const;
type Campo = (typeof CAMPOS)[number];

/**
 * "Ajustar" um item da revisão ANTES de importar — o que for corrigido aqui é o que entra
 * (ex.: escolher a Situação de um status do GLPI que não diz qual é, ou o colaborador quando o
 * usuário do GLPI não foi encontrado). Situação só pra item novo: em cadastro que já existe,
 * a Situação é do fluxo do Digital e a importação não mexe.
 */
export default function AjustarItemImportacaoModal({ item, decisao, onSalvar, onClose }: AjustarItemImportacaoModalProps) {
  const { data: regras = [] } = useRegrasClienteQuery();
  const { data: clientes = [] } = useClientsQuery();
  const [form, setForm] = useState<Record<Campo, string>>({} as Record<Campo, string>);

  useEffect(() => {
    if (!item) return;
    setForm(Object.fromEntries(CAMPOS.map((c) => [c, valorFinal(item, decisao, c) ?? ''])) as Record<Campo, string>);
  }, [item, decisao]);

  const clientesPropriedade = useMemo(() => {
    const nomePorId = new Map(clientes.map((c) => [c.id, c.name]));
    const ids = new Set(regras.filter((r) => r.permiteEquipamentoCliente).map((r) => r.clienteId));
    if (form.clienteProprietarioId) ids.add(form.clienteProprietarioId);
    return Array.from(ids).map((id) => ({ id, nome: nomePorId.get(id) ?? 'Cliente não encontrado' }));
  }, [regras, clientes, form.clienteProprietarioId]);

  if (!item) return null;
  const novo = item.acao === 'novo';
  const alterar = (campo: Campo, valor: string) =>
    setForm((atual) => {
      const novoForm = { ...atual, [campo]: valor };
      // Veio "não localizado" só por não achar a pessoa: escolhendo o colaborador, volta pra Em uso.
      if (
        campo === 'colaboradorAtualId' &&
        valor &&
        item.naoLocalizadoSemColaborador &&
        atual.statusCustodia === 'inativo' &&
        atual.situacaoDetalhe === 'nao_localizado'
      ) {
        novoForm.statusCustodia = 'com_colaborador';
        novoForm.situacaoDetalhe = '';
      }
      return novoForm;
    });
  const situacao: SituacaoAtivo | '' = form.statusCustodia ? SITUACAO_POR_STATUS[form.statusCustodia as keyof typeof SITUACAO_POR_STATUS] ?? '' : '';
  const detalhes = situacao ? DETALHES_SITUACAO_ATIVO[situacao] : undefined;
  const propriedadeValor = form.propriedade === 'cliente' ? form.clienteProprietarioId : form.propriedade;

  const salvar = () => {
    // Guarda só o que mudou em relação ao que veio do GLPI.
    const ajustes: Record<string, string | null> = {};
    for (const campo of CAMPOS) {
      if (!novo && (campo === 'statusCustodia' || campo === 'situacaoDetalhe')) continue;
      const original = ((item as unknown as Record<string, unknown>)[campo] as string | null) ?? '';
      if ((form[campo] ?? '') !== original) ajustes[campo] = form[campo] || null;
    }
    if (form.propriedade === 'cliente') ajustes.projetoClienteId = form.clienteProprietarioId || null;
    onSalvar(ajustes);
  };

  const input = (campo: Campo, label: string) => (
    <div>
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <input className="input-primary mt-1 w-full" value={form[campo] ?? ''} onChange={(e) => alterar(campo, e.target.value)} />
    </div>
  );

  return (
    <BaseModal
      isOpen={Boolean(item)}
      onClose={onClose}
      title={`Ajustar antes de importar — ${item.patrimonio ?? 'sem patrimônio'}`}
      maxWidth="max-w-2xl"
      fullHeight={false}
      actions={
        <div className="flex justify-end gap-3">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" onClick={salvar}>
            Salvar ajustes
          </button>
        </div>
      }
    >
      <div className="grid gap-3 py-4 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-700">Tipo</label>
          <select className="input-primary mt-1 w-full" value={form.tipo ?? ''} onChange={(e) => alterar('tipo', e.target.value)}>
            {TIPOS_ATIVO_CADASTRO.map((t) => (
              <option key={t} value={t}>
                {TIPO_ATIVO_LABELS[t]}
              </option>
            ))}
          </select>
        </div>
        {input('patrimonio', rotuloPatrimonio(form.tipo))}
        {input('nSerie', 'Nº de série')}
        {input('fabricante', 'Fabricante')}
        <div className="sm:col-span-2">{input('modelo', 'Modelo')}</div>

        {novo && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700">Situação</label>
              <select
                className="input-primary mt-1 w-full"
                value={situacao}
                onChange={(e) => {
                  alterar('statusCustodia', e.target.value ? STATUS_POR_SITUACAO[e.target.value as SituacaoAtivo] : '');
                  alterar('situacaoDetalhe', '');
                }}
              >
                <option value="">Em estoque (padrão)</option>
                {SITUACOES_ATIVO.map((s) => (
                  <option key={s} value={s}>
                    {SITUACAO_ATIVO_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
            {detalhes && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Detalhe</label>
                <select className="input-primary mt-1 w-full" value={form.situacaoDetalhe ?? ''} onChange={(e) => alterar('situacaoDetalhe', e.target.value)}>
                  <option value="">—</option>
                  {Object.entries(detalhes).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700">Propriedade</label>
          <select
            className="input-primary mt-1 w-full"
            value={propriedadeValor ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              alterar('propriedade', !v || v === 'raro' ? v : 'cliente');
              alterar('clienteProprietarioId', !v || v === 'raro' ? '' : v);
            }}
          >
            <option value="">Não informada</option>
            <option value="raro">Raro</option>
            {clientesPropriedade.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
              </option>
            ))}
          </select>
        </div>
        {form.propriedade === 'raro' && (
          <div>
            <label className="block text-sm font-medium text-gray-700">Projeto (Cliente em uso)</label>
            <div className="mt-1">
              <ClientCombobox selectedId={form.projetoClienteId || null} onChange={(c) => alterar('projetoClienteId', c?.id ?? '')} placeholder="Buscar cliente..." />
            </div>
          </div>
        )}
        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-gray-700">Colaborador</label>
          <div className="mt-1">
            <CollaboratorCombobox selectedId={form.colaboradorAtualId || null} onChange={(c) => alterar('colaboradorAtualId', c?.id ?? '')} placeholder="Buscar colaborador..." />
          </div>
          {item.usuarioGlpi && <p className="mt-1 text-xs text-gray-400">Usuário no GLPI: {item.usuarioGlpi}</p>}
        </div>
      </div>
    </BaseModal>
  );
}
